/**
 * Supabase Client Configuration for AdCanvas
 *
 * This module provides the authenticated Supabase client for frontend use.
 * It handles session persistence, token refresh, and auth state changes.
 */

import { createClient, Session, User } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from './info';

const supabaseUrl = `https://${projectId}.supabase.co`;

/**
 * Maximum time we will wait to acquire the cross-tab auth lock before giving up
 * and proceeding best-effort. This is the safety valve that prevents the
 * deadlock/AbortError class of bugs (supabase-js#1594) that a previous no-op
 * lock was introduced to dodge. A token refresh holds the lock for well under a
 * second, so a legitimate waiter always acquires inside this window.
 */
const MAX_LOCK_WAIT_MS = 10_000;

/**
 * Cross-tab auth lock backed by the Web Locks API.
 *
 * Supabase serializes token refresh across tabs via this lock. The client used
 * to pass a NO-OP lock, which meant nothing coordinated refresh between tabs:
 * with multiple tabs open they refreshed simultaneously, refresh-token rotation
 * tripped reuse detection, and the whole session got REVOKED — logging users out
 * and leaving "zombie" tabs whose data calls all 401 (e.g. McGee seeing zeros /
 * "couldn't reach the server" while the backend was healthy).
 *
 * This restores real serialization but can never hang auth: it waits at most
 * `acquireTimeout` (capped at MAX_LOCK_WAIT_MS) for the lock, and if it can't be
 * acquired in time — or Web Locks is unavailable (old browsers, SSR, tests) — it
 * proceeds best-effort by running `fn` directly. Worst case it degrades to the
 * old no-op behavior; the normal case prevents the multi-tab session wipe.
 */
export const navigatorLock = async <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    return await fn();
  }

  const timeoutMs =
    acquireTimeout > 0 ? Math.min(acquireTimeout, MAX_LOCK_WAIT_MS) : MAX_LOCK_WAIT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await navigator.locks.request(
      name,
      { mode: 'exclusive', signal: controller.signal },
      async () => await fn(),
    );
  } catch (err) {
    // Couldn't acquire the lock in time → don't deadlock; run best-effort.
    if ((err as { name?: string } | null)?.name === 'AbortError') {
      return await fn();
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * The main Supabase client instance.
 * This client automatically handles:
 * - Session persistence in localStorage
 * - Automatic token refresh (serialized across tabs via navigatorLock)
 * - Auth state change events
 */
export const supabase = createClient(supabaseUrl, publicAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    /**
     * Emailed auth links (invite + password recovery) MUST survive being opened
     * in a different browser than the one that requested them.
     *
     * supabase-js v2 defaults to PKCE. Under PKCE the client stores a
     * `code_verifier` in the localStorage of the browser that initiated the
     * request, and the emailed link comes back as `?code=...` which is useless
     * without that verifier. But people request a reset on their laptop and
     * then click the link from Outlook, or from their phone — a different
     * storage origin every time — so `exchangeCodeForSession` fails and the
     * user is told the link is "invalid or expired" even though the token was
     * perfectly good. The token really is consumed server-side (GoTrue logs a
     * `login`), so the account looks fine from the backend while the user can
     * never actually finish setting a password. That is exactly the loop an
     * invited McGee user hit: four recovery requests, four server-side logins,
     * zero password updates.
     *
     * Implicit flow puts the tokens in the URL fragment instead, which is
     * self-contained and therefore portable across browsers and devices. The
     * fragment is never sent to a server, and both auth pages strip it from the
     * URL via history.replaceState once the session is established. AdCanvas is
     * a static SPA with no server-side code exchange, so PKCE was buying us
     * nothing here while breaking the most common real-world path.
     */
    flowType: 'implicit',
    lock: navigatorLock,
  },
});

/**
 * Get the current session if it exists.
 * @returns The current session or null
 */
export async function getSession(): Promise<Session | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Get the current user if authenticated.
 * @returns The current user or null
 */
export async function getUser(): Promise<User | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Get headers for authenticated API requests.
 * This returns the appropriate headers to include in fetch requests
 * to edge functions that require authentication.
 *
 * @returns Headers object with Authorization token if authenticated
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getSession();
  // Get the currently selected organization from localStorage
  const currentOrgId = localStorage.getItem('adcanvas_current_org');

  if (session?.access_token) {
    const headers: HeadersInit = {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    };
    // Add organization ID if one is selected
    if (currentOrgId) {
      (headers as Record<string, string>)['X-Organization-Id'] = currentOrgId;
    }
    return headers;
  }

  // Fall back to anon key if no session (for unauthenticated endpoints)
  return {
    'Authorization': `Bearer ${publicAnonKey}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Sign in with email and password.
 */
export async function signIn(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

/**
 * Sign up with email and password.
 */
export async function signUp(email: string, password: string) {
  return supabase.auth.signUp({ email, password });
}

/**
 * Sign out the current user.
 */
export async function signOut() {
  return supabase.auth.signOut();
}

/**
 * Send a password reset email.
 */
export async function resetPassword(email: string) {
  return supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
}

/**
 * Update the user's password.
 */
export async function updatePassword(newPassword: string) {
  return supabase.auth.updateUser({ password: newPassword });
}

export default supabase;
